/**
 * Messages (décision du propriétaire du 25 octobre 2026) : une rubrique limitée,
 * annonces de l'entreprise et conversations entre membres internes. Pas de clone de
 * messagerie instantanée : ni « en train d'écrire », ni vocal, ni appel, ni
 * réaction, ni temps réel (push et rafraîchissement à l'ouverture suffisent), et
 * pas d'accusé « Vu » dans les conversations ; « vu par X sur Y » pour les annonces
 * seulement.
 *
 * Bornes et schémas partagés par l'API (contrôle) et l'application (aide à la
 * saisie). Les droits sont `messaging.use` (conversations, lecture des annonces)
 * et `announcement.publish` (publier) ; l'entreprise peut couper toute la rubrique
 * (`company_settings.messaging_enabled`, active par défaut).
 */
import { z } from 'zod';

export const MESSAGING_LIMITS = {
  /** Texte d'un message, en caractères. */
  bodyMax: 4000,
  /** Pièces jointes (photos, PDF) par message. */
  filesPerMessage: 6,
  /** Fiches jointes (intervention, devis…) par message. */
  refsPerMessage: 3,
  /** Messages envoyés par personne et par heure, toutes conversations confondues. */
  messagesPerHour: 120,
  /** Membres d'un groupe, créateur compris. */
  groupMembersMax: 20,
  /** Conversations actives par personne. */
  conversationsPerUser: 200,
  /**
   * Groupes créés par personne et par heure : un collègue ne remplit pas seul, en
   * créant des groupes à la chaîne, le plafond de conversations d'un autre.
   */
  groupsPerHour: 10,
  /** Nom facultatif d'un groupe. */
  titleMax: 80,
  /** Un message se retire pendant ce délai, jamais au-delà ; il ne se modifie pas. */
  withdrawMinutes: 15,
  /** Messages par page d'une conversation. */
  pageSize: 50,
  /** Taille d'une pièce jointe : celle que la visionneuse lit encore (images et PDF). */
  fileMaxBytes: 20 * 1024 * 1024,
  /** Annonces publiées par entreprise et par jour. */
  announcementsPerDay: 10,
  announcementTitleMax: 120,
  announcementBodyMax: 5000,
  /** Pièces jointes d'une annonce. */
  announcementFilesMax: 4,
  /** Rôles ou membres choisis comme audience d'une annonce. */
  audienceRolesMax: 30,
  audienceMembersMax: 500,
  /** Une relance de lecture d'annonce par période. */
  reminderCooldownHours: 24,
  /** Accusé fait hors ligne : son heure d'appareil n'est acceptée que dans ce délai. */
  offlineAckMaxAgeDays: 7,
  /** Texte montré dans une notification sur écran verrouillé : « Prénom : … ». */
  pushPreviewChars: 100,
} as const;

/** Conservation des messages (classe `messagerie`) : 24 mois par défaut, de 1 mois à 10 ans. */
export const MESSAGING_RETENTION_MONTHS = { default: 24, min: 1, max: 120 } as const;

/**
 * Sorte des fichiers joints à un message ou à une annonce, posée à l'envoi. Seuls
 * les membres de la conversation (ou les destinataires de l'annonce) les lisent.
 */
export const MESSAGE_FILE_KIND = 'piece_message';

export const CONVERSATION_KINDS = ['direct', 'groupe'] as const;
export type ConversationKind = (typeof CONVERSATION_KINDS)[number];

/**
 * Fiches qu'un message peut joindre. Un message ne garde que la sorte et
 * l'identifiant : le libellé se résout pour chaque lecteur selon ses droits, et une
 * fiche qu'il ne peut pas lire ne révèle ni son nom ni son existence.
 */
export const REFERENCE_KINDS = [
  'intervention', 'quote', 'invoice', 'contract', 'customer', 'equipment', 'supplier_invoice',
] as const;
export type ReferenceKind = (typeof REFERENCE_KINDS)[number];

/** Audience d'une annonce : toute l'équipe, un ou plusieurs rôles, ou des membres choisis. */
export const ANNOUNCEMENT_AUDIENCE_KINDS = ['tous', 'roles', 'membres'] as const;
export type AnnouncementAudienceKind = (typeof ANNOUNCEMENT_AUDIENCE_KINDS)[number];

/**
 * Clé d'une conversation à deux : la même quel que soit celui qui l'ouvre, pour
 * qu'il n'en existe qu'une par paire (index unique `conversations.direct_key`).
 */
export function directConversationKey(a: string, b: string): string {
  const [first, second] = [a.toLowerCase(), b.toLowerCase()].sort();
  return `${first}:${second}`;
}

/** Liste d'identifiants sans doublon. */
const uniqueIds = (max: number, min = 0) => z
  .array(z.guid())
  .min(min)
  .max(max)
  .refine((ids) => new Set(ids.map((id) => id.toLowerCase())).size === ids.length, 'Élément en double.');

export const messageReference = z.object({
  kind: z.enum(REFERENCE_KINDS),
  id: z.guid(),
}).strict();
export type MessageReference = z.infer<typeof messageReference>;

/**
 * Envoi d'un message. L'identifiant vient de l'appareil : un envoi rejoué (hors
 * ligne, réseau coupé) retrouve le même message au lieu d'en créer un second.
 */
export const messageSendBody = z.object({
  id: z.guid(),
  body: z.string().trim().max(MESSAGING_LIMITS.bodyMax).default(''),
  fileIds: uniqueIds(MESSAGING_LIMITS.filesPerMessage).default([]),
  refs: z.array(messageReference).max(MESSAGING_LIMITS.refsPerMessage)
    .refine((refs) => new Set(refs.map((ref) => `${ref.kind}:${ref.id.toLowerCase()}`)).size === refs.length,
      'Fiche jointe en double.')
    .default([]),
}).strict().refine(
  (message) => message.body.length > 0 || message.fileIds.length > 0 || message.refs.length > 0,
  { error: 'Écrivez un message ou joignez une pièce.' },
);
export type MessageSendBody = z.infer<typeof messageSendBody>;

const conversationTitle = z.string().trim().min(1).max(MESSAGING_LIMITS.titleMax);

/**
 * Nouvelle conversation : une personne (conversation à deux, retrouvée si elle
 * existe déjà) ou plusieurs (groupe, nom facultatif). Le créateur n'est pas compté.
 */
export const conversationCreateBody = z.object({
  id: z.guid().optional(),
  memberIds: uniqueIds(MESSAGING_LIMITS.groupMembersMax - 1, 1),
  title: conversationTitle.optional(),
}).strict();
export type ConversationCreateBody = z.infer<typeof conversationCreateBody>;

/** Nom du groupe (null le retire) et sourdine personnelle. */
export const conversationUpdateBody = z.object({
  title: conversationTitle.nullable().optional(),
  muted: z.boolean().optional(),
}).strict().refine((change) => change.title !== undefined || change.muted !== undefined, {
  error: 'Indiquez le nom du groupe ou la sourdine.',
});
export type ConversationUpdateBody = z.infer<typeof conversationUpdateBody>;

/** Lu jusqu'à ce message (numéro d'ordre dans la conversation). */
export const conversationReadBody = z.object({
  upToSeq: z.number().int().min(0),
}).strict();

/** Membres ajoutés à un groupe. */
export const conversationMembersBody = z.object({
  memberIds: uniqueIds(MESSAGING_LIMITS.groupMembersMax - 1, 1),
}).strict();

export const announcementAudience = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('tous') }).strict(),
  z.object({ kind: z.literal('roles'), ids: uniqueIds(MESSAGING_LIMITS.audienceRolesMax, 1) }).strict(),
  z.object({ kind: z.literal('membres'), ids: uniqueIds(MESSAGING_LIMITS.audienceMembersMax, 1) }).strict(),
]);
export type AnnouncementAudience = z.infer<typeof announcementAudience>;

/**
 * Publication d'une annonce. Une annonce publiée ne change plus : la corriger,
 * c'est l'archiver et en publier une autre qui la remplace (`replacesId`).
 */
export const announcementCreateBody = z.object({
  title: z.string().trim().min(1).max(MESSAGING_LIMITS.announcementTitleMax),
  body: z.string().trim().min(1).max(MESSAGING_LIMITS.announcementBodyMax),
  audience: announcementAudience,
  requiresAck: z.boolean().default(false),
  /** Passe outre les horaires calmes ; tracé au journal d'audit. */
  urgent: z.boolean().default(false),
  fileIds: uniqueIds(MESSAGING_LIMITS.announcementFilesMax).default([]),
  replacesId: z.guid().optional(),
}).strict();
export type AnnouncementCreateBody = z.infer<typeof announcementCreateBody>;
